/**
 * Seam-crossing compliance decision (RD-27 §5).
 *
 * Given a seam regime's required-witness tuple and the set of witnesses
 * an agent currently holds, decides whether the crossing is structurally
 * compliant (constructable) or unconstructable, and names exactly which
 * required witnesses are absent.
 *
 * This mirrors ABK-4 (rwt-sufficient): exhibiting every required witness
 * is sufficient for structural compliance. Absent any required witness,
 * the crossing is structurally inexpressible — not "rejected by a guard".
 *
 * Companion to agda/Research/AgenticBoundaryKernel/SeamObligations.agda.
 * React-free and pure.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { ComplianceVerdict, WitnessId } from "./types.js";

export type { ComplianceVerdict, WitnessId } from "./types.js";

/**
 * Evaluate a crossing against the held witnesses.
 *
 * @param regimeId      the seam regime under evaluation
 * @param required      the regime's required-witness tuple
 * @param held          the witnesses the agent currently holds
 */
export function evaluateCrossing(
  regimeId: string,
  required: readonly WitnessId[],
  held: readonly WitnessId[],
): ComplianceVerdict {
  const heldSet = new Set<WitnessId>(held);
  const present: WitnessId[] = [];
  const missing: WitnessId[] = [];
  for (const witness of required) {
    if (heldSet.has(witness)) {
      present.push(witness);
    } else {
      missing.push(witness);
    }
  }
  return {
    regimeId,
    required,
    present,
    missing,
    constructable: missing.length === 0,
  };
}
